import React, { useState, useCallback } from 'react';
import Chart from 'react-apexcharts';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import ResponsiveTable from './ResponsiveTable';

const UNIFIED_BAR_OPTIONS = (categories, id) => ({
  chart: { id, toolbar: { show: false }, background: 'transparent' },
  theme: { mode: 'dark' },
  xaxis: {
    categories,
    labels: { style: { fontSize: '12px', colors: '#94a3b8' } },
    axisBorder: { show: false },
    axisTicks: { show: false },
  },
  yaxis: {
    labels: { show: false },
    axisTicks: { show: false },
    axisBorder: { show: false },
  },
  dataLabels: {
    enabled: true,
    formatter: (val) => val.toLocaleString(),
    offsetY: -20,
    style: { fontSize: '11px', colors: ['#94a3b8'] },
  },
  plotOptions: {
    bar: { dataLabels: { position: 'top' }, borderRadius: 4 },
  },
  fill: { colors: ['#0ea5e9'] },
  grid: { borderColor: 'rgba(255,255,255,0.05)', padding: { left: 10, right: 0 } },
  tooltip: { theme: 'dark', y: { formatter: (val) => val.toLocaleString() } },
  responsive: [
    {
      breakpoint: 640,
      options: {
        chart: { height: 250, toolbar: { show: false } },
        yaxis: [{ labels: { show: false }, axisTicks: { show: false }, axisBorder: { show: false } }],
        grid: { padding: { left: 24 } },
      },
    },
  ],
});

function ByAlbumsTable({ data, totalCount, currentPage, pageSize, me }) {
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);

  const handleAlbumClick = useCallback(
    async (album) => {
      setSelectedAlbum(album);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/ByAlbum/getMonthlyAlbumAmounts`,
          {
            params: {
              albumId: album.album.id,
              clientId: me?.id,
              adminYn: me?.adminYn,
            },
          }
        );
        setMonthlyData(res.data);
      } catch (error) {
        console.error('차트 데이터 불러오기 실패:', error);
      }
    },
    [me]
  );

  const columns = React.useMemo(() => {
    if (!me) return [];

    return [
      {
        Header: () => <div className="whitespace-nowrap text-center">순번</div>,
        id: 'row',
        Cell: ({ row }) => (
          <div className="text-center whitespace-nowrap">
            {totalCount - ((currentPage - 1) * pageSize + row.index)}
          </div>
        ),
      },
      {
        Header: '커버',
        accessor: 'album.image',
        Cell: ({ row }) => (
          <img
            src={
              row.original.album.image ||
              'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/images/none_image.jpg'
            }
            alt={row.original.nameKr}
            className="w-12 h-12 rounded-full object-cover mx-auto border border-white/[0.08]"
          />
        ),
      },
      {
        Header: '앨범명',
        accessor: 'album.nameKr',
        Cell: ({ row }) => (
          <span
            className="font-bold text-sky-600 cursor-pointer"
            onClick={() => handleAlbumClick(row.original)}>
            {row.original.album.nameKr}
          </span>
        ),
      },
      { Header: '아티스트', accessor: 'album.artist.nameKr' },
      ...(me?.adminYn === 'Y'
        ? [
            {
              Header: '권리사',
              accessor: 'album.displayClient',
              Cell: ({ value }) => <span>{value || '-'}</span>,
            },
          ]
        : []),
      {
        Header: '총 히트수',
        accessor: 'albumHitCount',
        Cell: ({ value }) => Number(value).toLocaleString(),
      },
      {
        Header: '총 정산액',
        accessor: 'albumAmount',
        Cell: ({ value }) => <b>{Number(value).toLocaleString()}</b>,
      },
    ];
  }, [totalCount, currentPage, pageSize, handleAlbumClick, me]);

  return (
    <>
      <ResponsiveTable
        columns={columns}
        data={data}
        visibleOnMobile={[
          'row',
          'album.nameKr',
          'album.artist.nameKr',
          'albumAmount',
        ]}
      />

      {selectedAlbum && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
          onClick={() => setSelectedAlbum(null)}>
          <div
            className="bg-[#1a2b42] p-6 rounded-xl shadow-xl border border-white/10 w-full max-w-2xl relative"
            onClick={(e) => e.stopPropagation()}>
            <button
              className="absolute top-2 right-2 text-slate-400 hover:text-white"
              onClick={() => setSelectedAlbum(null)}>
              <FontAwesomeIcon icon={faXmark} size="lg" />
            </button>
            <h2 className="text-xl font-bold mb-4 text-white">
              "{selectedAlbum.album.nameKr}" 최근 1년 정산 그래프
            </h2>
            <Chart
              type="bar"
              width="100%"
              height="300"
              series={[
                {
                  name: '월별 정산액',
                  data: monthlyData.map((item) =>
                    me?.adminYn === 'Y'
                      ? item.amount
                      : item.clientAmount ?? item.amount
                  ),
                },
              ]}
              options={UNIFIED_BAR_OPTIONS(
                monthlyData.map((item) => item.month),
                'album-month-chart'
              )}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default ByAlbumsTable;
